// src/components/roleplay/History.js
// Lists the current rep's past roleplay sessions, newest first.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { subscribeMySessions } from '../../data/roleplay';
import { trackLabel, difficultyLabel, formatUsd } from '../../config/roleplay';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px;
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PersonaTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
  white-space: nowrap;
`;

const DateText = styled.span`
  font-size: 12px;
  color: #94a3b8;
`;

const ScoreBlock = styled.div`
  display: flex;
  align-items: baseline;
  gap: 3px;
  flex-shrink: 0;
`;

const ScoreNumber = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: #0e8c6a;
  line-height: 1;
`;

const ScoreOutOf = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
`;

const CoachingTip = styled.div`
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
  border-top: 1px solid #f1f5f9;
  padding-top: 10px;
  margin-top: 4px;
`;

const CoachingLabel = styled.span`
  font-weight: 700;
  color: #0e8c6a;
  margin-right: 6px;
`;

const EmptyState = styled.div`
  color: #64748b;
  font-size: 14px;
  padding: 40px 0;
  text-align: center;
  background: #ffffff;
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
`;

const ErrorState = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px 20px;
  border-radius: 12px;
  font-size: 14px;
`;

function formatDate(createdAt) {
  if (!createdAt) return '';
  try {
    const d = typeof createdAt.toDate === 'function' ? createdAt.toDate() : new Date(createdAt);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

function History({ user }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.email) return undefined;
    setLoading(true);
    const unsub = subscribeMySessions(
      user.email,
      (rows) => {
        setSessions(rows);
        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(
          err?.code === 'permission-denied'
            ? "You don't have permission to view session history."
            : 'Could not load your session history. Please refresh and try again.'
        );
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user?.email]);

  if (loading) {
    return <EmptyState>Loading your sessions...</EmptyState>;
  }

  if (error) {
    return <ErrorState>{error}</ErrorState>;
  }

  if (sessions.length === 0) {
    return <EmptyState>No practice sessions yet — head to the Practice tab to get started.</EmptyState>;
  }

  return (
    <List>
      {sessions.map((s) => (
        <Card key={s.id}>
          <CardTop>
            <TitleBlock>
              <PersonaTitle>{s.personaTitle || 'Untitled persona'}</PersonaTitle>
              <MetaRow>
                {s.track && <Badge>{trackLabel(s.track)}</Badge>}
                {s.difficulty && <Badge>{difficultyLabel(s.difficulty)}</Badge>}
                {typeof s.costUsd === 'number' && <Badge>{formatUsd(s.costUsd)}</Badge>}
                <DateText>{formatDate(s.createdAt)}</DateText>
              </MetaRow>
            </TitleBlock>
            {typeof s.overall === 'number' && (
              <ScoreBlock>
                <ScoreNumber>{s.overall.toFixed(1)}</ScoreNumber>
                <ScoreOutOf>/ 5</ScoreOutOf>
              </ScoreBlock>
            )}
          </CardTop>
          {s.coachingTip && (
            <CoachingTip>
              <CoachingLabel>Coaching tip:</CoachingLabel>
              {s.coachingTip}
            </CoachingTip>
          )}
        </Card>
      ))}
    </List>
  );
}

export default History;
